/**
 * Minimal OpenAI-compatible client for the Hub SPA.
 *
 * The chat playground talks to the Hub session proxy at `/api/chat/completions`
 * (authenticated by the Hub session cookie + selected `keyId`); the SPA never
 * sees raw API keys. `listModels()` still talks to the public router
 * `/v1/models` endpoint because that data is non-secret and used by the
 * Docs/Models & Pricing tabs.
 *
 * Why the chat call lives outside `src/api/*`:
 *   `src/api/*` is request/response; the chat call is a long-lived SSE
 *   stream that needs a streaming `fetch` reader and per-delta dispatch.
 *   Keeping the SSE parser here avoids pulling stream concerns into the
 *   generic Hub client.
 *
 * Base URL for `/v1/models` is the router base (build-time env or same
 * origin); the chat call always uses the same-origin Hub path.
 */

const ROUTER_BASE: string = (() => {
  const fromEnv = import.meta.env.VITE_ROUTER_URL;
  if (typeof fromEnv === 'string' && fromEnv.length > 0) {
    // Trim trailing slash so `${ROUTER_BASE}/v1/...` never doubles up.
    return fromEnv.replace(/\/+$/, '');
  }
  return '';
})();

export function routerBaseUrl(): string {
  return ROUTER_BASE;
}

// ---------------------------------------------------------------------------
// /v1/models
// ---------------------------------------------------------------------------

export interface OpenAIModel {
  id: string;
  object: 'model';
  created: number;
  owned_by: string;
  display_name?: string;
  context_window?: number;
}

interface ModelsResponse {
  object: 'list';
  data: OpenAIModel[];
}

export async function listModels(signal?: AbortSignal): Promise<OpenAIModel[]> {
  const res = await fetch(`${ROUTER_BASE}/v1/models`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`GET /v1/models failed: ${res.status} ${body}`);
  }
  const json = (await res.json()) as ModelsResponse;
  return json.data ?? [];
}

// ---------------------------------------------------------------------------
// /v1/chat/completions (streaming)
// ---------------------------------------------------------------------------

export interface ChatMessageParam {
  role: 'system' | 'user' | 'assistant' | 'tool';
  content?: string | null;
  name?: string;
  tool_call_id?: string;
  tool_calls?: ToolCall[];
}

export interface ToolCall {
  /** Index within the delta array; useful while accumulating streaming chunks. */
  index?: number;
  id?: string;
  type?: 'function';
  function: { name?: string; arguments?: string };
}

export interface ChatTool {
  type: 'function';
  function: {
    name: string;
    description?: string;
    parameters?: Record<string, unknown>;
  };
}

export interface ChatCompletionRequest {
  model: string;
  messages: ChatMessageParam[];
  temperature?: number;
  max_tokens?: number;
  stream?: boolean;
  tools?: ChatTool[];
  tool_choice?: 'auto' | 'none' | 'required';
  parallel_tool_calls?: boolean;
}

/** A single token delta as decoded from `data: { … }` SSE lines. */
export interface StreamDelta {
  /** Assistant content chunk. */
  content?: string;
  /** Reasoning/thinking content (Kimi K2.6, DeepSeek R1, OpenAI o1). */
  reasoning?: string;
  /** Tool-call delta(s). Each one may carry partial `function.arguments`. */
  tool_calls?: ToolCall[];
  /** Finish reason on the last chunk (e.g. 'stop', 'tool_calls'). */
  finish_reason?: string | null;
}

export interface StreamHandlers {
  onDelta: (delta: StreamDelta) => void;
  onError: (err: { status: number; message: string; retryable: boolean }) => void;
  onDone: () => void;
}

/**
 * Stream `/api/chat/completions` from the Hub session proxy and dispatch
 * parsed deltas. The Hub forwards to the router via the trusted internal
 * auth path, so the SPA only needs the Hub session cookie + `keyId`.
 *
 * Handles three failure modes mirroring the keeper's `parseAiStream`:
 *   1. Non-200 HTTP (4xx/5xx).
 *   2. HTTP 200 with `application/json` (Hub collapsed the SSE — usually
 *      an error envelope).
 *   3. HTTP 200 + SSE with mid-stream `{"error": …}` payload.
 */
export async function streamHubChatCompletion(
  keyId: string,
  body: ChatCompletionRequest,
  handlers: StreamHandlers,
  signal?: AbortSignal,
): Promise<void> {
  const url = '/api/chat/completions';
  let res: Response;
  try {
    res = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'text/event-stream',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ...body, key_id: keyId, stream: true }),
      signal,
    });
  } catch (err) {
    if ((err as { name?: string }).name === 'AbortError') {
      handlers.onDone();
      return;
    }
    handlers.onError({ status: 0, message: (err as Error).message || 'Network error', retryable: true });
    return;
  }

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    const parsed = safeParseErrorBody(text);
    handlers.onError({
      status: res.status,
      message: parsed ?? `HTTP ${res.status}`,
      retryable: res.status === 429 || res.status >= 500,
    });
    return;
  }

  const contentType = (res.headers.get('content-type') || '').toLowerCase();
  if (!contentType.includes('event-stream')) {
    // Router collapsed the SSE into a JSON envelope — usually an error.
    const text = await res.text().catch(() => '');
    const parsed = safeParseErrorBody(text);
    handlers.onError({
      status: res.status,
      message: parsed ?? 'Upstream returned non-SSE response',
      retryable: false,
    });
    return;
  }

  if (!res.body) {
    handlers.onError({ status: res.status, message: 'Empty response body', retryable: false });
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE frames are separated by blank lines; within a frame, fields are
      // newline-delimited. We split by single newline and process each
      // `data: …` line individually, which is enough for OpenAI's wire
      // format (it never spreads a JSON payload across multiple data: lines).
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      for (const raw of lines) {
        const line = raw.trim();
        if (!line || line.startsWith(':')) continue;
        if (!line.startsWith('data:')) continue;
        const payload = line.slice(5).trim();
        if (payload === '[DONE]') {
          handlers.onDone();
          return;
        }
        let json: any;
        try {
          json = JSON.parse(payload);
        } catch {
          continue;
        }
        // Mid-stream error payload.
        if (json && typeof json === 'object' && 'error' in json && json.error) {
          const message =
            (typeof json.error === 'string' && json.error) ||
            (json.error?.message as string) ||
            'Upstream error';
          handlers.onError({ status: 500, message, retryable: false });
          return;
        }

        const choice = json?.choices?.[0];
        if (!choice) continue;
        const delta = choice.delta ?? {};
        const out: StreamDelta = {};
        if (typeof delta.content === 'string' && delta.content.length > 0) {
          out.content = delta.content;
        }
        const reasoning =
          (typeof delta.reasoning_content === 'string' && delta.reasoning_content) ||
          (typeof delta.reasoning === 'string' && delta.reasoning) ||
          (typeof delta.thinking === 'string' && delta.thinking) ||
          null;
        if (reasoning) out.reasoning = reasoning;
        if (Array.isArray(delta.tool_calls) && delta.tool_calls.length > 0) {
          out.tool_calls = delta.tool_calls as ToolCall[];
        }
        if (typeof choice.finish_reason === 'string' || choice.finish_reason === null) {
          out.finish_reason = choice.finish_reason;
        }
        if (out.content || out.reasoning || out.tool_calls || out.finish_reason) {
          handlers.onDelta(out);
        }
      }
    }
  } catch (err) {
    if ((err as { name?: string }).name === 'AbortError') {
      handlers.onDone();
      return;
    }
    handlers.onError({ status: 0, message: (err as Error).message || 'Stream interrupted', retryable: true });
    return;
  } finally {
    try {
      reader.releaseLock();
    } catch {
      /* already released */
    }
  }
  handlers.onDone();
}

function safeParseErrorBody(text: string): string | null {
  if (!text) return null;
  try {
    const obj = JSON.parse(text) as unknown;
    if (obj && typeof obj === 'object') {
      const o = obj as Record<string, unknown>;
      if (typeof o.error === 'string') return o.error;
      if (o.error && typeof o.error === 'object') {
        const e = o.error as Record<string, unknown>;
        if (typeof e.message === 'string') return e.message;
      }
      if (typeof o.message === 'string') return o.message;
    }
  } catch {
    return text.slice(0, 300);
  }
  return null;
}

/**
 * Dummy `echo_value` tool used by the playground when the user toggles
 * tool-calling on. Mirrors the shape exercised by
 * `test/unit/chat-tool-fields.test.ts`.
 */
export const ECHO_VALUE_TOOL: ChatTool = {
  type: 'function',
  function: {
    name: 'echo_value',
    description: 'Echo a provided value. Useful for smoke-testing tool calling.',
    parameters: {
      type: 'object',
      properties: { value: { type: 'string' } },
      required: ['value'],
    },
  },
};
